/**
 * Translation loader service for loading translations from database
 */
export class TranslationLoader {
  private static cache = new Map<string, Record<string, string>>();
  private static cacheTimestamp = new Map<string, number>();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Load translations for a specific language
   * Note: This fetches directly from the API endpoint since tRPC client
   * requires React context and this is called outside of components
   */
  static async loadTranslations(language: string): Promise<Record<string, string>> {
    try {
      // Check cache first
      if (this.isCacheValid(language)) {
        const cached = this.cache.get(language);
        if (cached) {
          return cached;
        }
      }

      // Load from API using direct fetch (tRPC client needs React context)
      const baseUrl = import.meta.env.VITE_API_URL || 'https://scsapi.itms.solutions';

      const response = await fetch(
        `${baseUrl}/trpc/system.translations.getActiveTranslations?input=${encodeURIComponent(
          JSON.stringify({ json: { language } }),
        )}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = (await response.json()) as any;
      const result = data?.result?.data?.json;

      if (result?.success && result?.data) {
        // Cache the result
        this.cache.set(language, result.data);
        this.cacheTimestamp.set(language, Date.now());

        return result.data;
      }

      return {};
    } catch (error) {
      console.warn(`Failed to load translations for language ${language}:`, error);

      // Return cached version if available, even if expired
      const cached = this.cache.get(language);
      if (cached) {
        return cached;
      }

      return {};
    }
  }

  /**
   * Load translations for multiple languages
   */
  static async loadMultipleLanguages(
    languages: string[],
  ): Promise<Record<string, Record<string, string>>> {
    const results: Record<string, Record<string, string>> = {};

    // Load all languages in parallel
    const promises = languages.map(async (language) => {
      const translations = await this.loadTranslations(language);
      return { language, translations };
    });

    const resolved = await Promise.allSettled(promises);

    resolved.forEach((result, index) => {
      const language = languages[index];
      if (result.status === 'fulfilled') {
        results[language] = result.value.translations;
      } else {
        console.warn(`Failed to load translations for ${language}:`, result.reason);
        results[language] = {};
      }
    });

    return results;
  }

  /**
   * Check if cached translations are still valid
   */
  private static isCacheValid(language: string): boolean {
    const timestamp = this.cacheTimestamp.get(language);
    if (!timestamp) return false;

    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  /**
   * Clear cache for a specific language
   */
  static clearCache(language?: string): void {
    if (language) {
      this.cache.delete(language);
      this.cacheTimestamp.delete(language);
    } else {
      this.cache.clear();
      this.cacheTimestamp.clear();
    }
  }

  /**
   * Preload translations for common languages
   */
  static async preloadCommonLanguages(): Promise<void> {
    const commonLanguages = ['en', 'ru', 'kk'];

    try {
      await this.loadMultipleLanguages(commonLanguages);
    } catch (error) {
      console.warn('Failed to preload common languages:', error);
    }
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): {
    size: number;
    languages: string[];
    oldestEntry: number | null;
    newestEntry: number | null;
  } {
    const languages = Array.from(this.cache.keys());
    const timestamps = Array.from(this.cacheTimestamp.values());

    return {
      size: this.cache.size,
      languages,
      oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null,
      newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : null,
    };
  }

  /**
   * Force refresh translations for a language
   */
  static async refreshTranslations(language: string): Promise<Record<string, string>> {
    this.clearCache(language);
    return await this.loadTranslations(language);
  }

  /**
   * Merge file-based and database translations
   */
  static mergeTranslations(
    fileTranslations: Record<string, string>,
    dbTranslations: Record<string, string>,
  ): Record<string, string> {
    // Database translations take precedence over file-based ones
    return {
      ...fileTranslations,
      ...dbTranslations,
    };
  }

  /**
   * Convert nested translation object to flat key-value pairs
   */
  static flattenTranslations(obj: Record<string, any>, prefix = ''): Record<string, string> {
    const result: Record<string, string> = {};

    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'string') {
        result[fullKey] = value;
      } else if (typeof value === 'object' && value !== null) {
        Object.assign(result, this.flattenTranslations(value, fullKey));
      }
    }

    return result;
  }

  /**
   * Convert flat translations back to nested object
   */
  static unflattenTranslations(flat: Record<string, string>): Record<string, any> {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(flat)) {
      const keys = key.split('.');
      let current = result;

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in current)) {
          current[k] = {};
        }
        current = current[k];
      }

      current[keys[keys.length - 1]] = value;
    }

    return result;
  }
}

/**
 * Custom resource backend for i18next that loads from database
 */
export class DatabaseResourceBackend {
  type = 'backend';
  services: any;
  options: any;

  init(services: any, backendOptions: any, i18nextOptions: any) {
    this.services = services;
    this.options = backendOptions;
  }

  read(language: string, namespace: string, callback: Function) {
    TranslationLoader.loadTranslations(language)
      .then((translations) => {
        // Filter by namespace if specified
        if (namespace && namespace !== 'translation') {
          const namespacedTranslations: Record<string, string> = {};
          const prefix = `${namespace}.`;

          for (const [key, value] of Object.entries(translations)) {
            if (key.startsWith(prefix)) {
              namespacedTranslations[key.substring(prefix.length)] = value;
            }
          }

          callback(null, namespacedTranslations);
        } else {
          // Convert flat translations to nested object for i18next
          const nested = TranslationLoader.unflattenTranslations(translations);
          callback(null, nested);
        }
      })
      .catch((error) => {
        console.error(`Failed to load translations for ${language}:`, error);
        callback(error, null);
      });
  }

  save(language: string, namespace: string, data: Record<string, string>) {
    // This would be used for saving translations back to database
    // For now, we'll just log it as this should be handled through the admin interface
    console.log(`Save request for ${language}/${namespace}:`, data);
  }
}

export default TranslationLoader;
